import { useEffect, useMemo, useState } from "react";
import { Camera, Upload, X, Plus, Trash2, Paperclip } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, todayIso, PAY_METHODS, TAX_MODES, inputCls, labelCls } from "./accountingUtils";

const emptyLine = () => ({ amount: "", category: "", who_for: "", description: "" });
const emptyForm = (taxRate) => ({
  date: todayIso(),
  payee: "",
  payment_method: "credit",
  account: "",
  lines: [emptyLine()],
  tax_mode: "none",
  tax_rate: String(taxRate ?? 13),
  ref_no: "",
  memo: "",
  receipt: null, // data URL | "keep" | null
});

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

const calcTotals = (form) => {
  const subtotal = form.lines.reduce((s, l) => s + num(l.amount), 0);
  const rate = form.tax_mode === "none" ? 0 : num(form.tax_rate) / 100;
  if (form.tax_mode === "excluded") {
    const tax = subtotal * rate;
    return { subtotal, tax, total: subtotal + tax };
  }
  if (form.tax_mode === "included") {
    const tax = rate ? subtotal - subtotal / (1 + rate) : 0;
    return { subtotal, tax, total: subtotal };
  }
  return { subtotal, tax: 0, total: subtotal };
};

/**
 * PersonalExpenseDialog — new / edit expense for the Home → Accounting tab.
 * Type, Who-for and Description are free-text (with datalist memory of past
 * entries). "Tax is" is a plain dropdown.
 */
const PersonalExpenseDialog = ({ base, bankBase, fromTx, open, onClose, initial, defaultTaxRate, onSaved }) => {
  const [form, setForm] = useState(() => emptyForm(defaultTaxRate));
  const [saving, setSaving] = useState(false);
  const [sugg, setSugg] = useState({ payees: [], accounts: [], categories: [], who_for: [] });
  const isEdit = !!initial?.expense_id;
  const totals = useMemo(() => calcTotals(form), [form]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        date: initial.date || todayIso(),
        payee: initial.payee || "",
        payment_method: initial.payment_method || "credit",
        account: initial.account || "",
        lines: (initial.lines?.length ? initial.lines : [emptyLine()]).map((l) => ({
          amount: l.amount != null ? String(l.amount) : "",
          category: l.category || "",
          who_for: l.who_for || "",
          description: l.description || "",
        })),
        tax_mode: initial.tax_mode || "none",
        tax_rate: String(initial.tax_rate ?? defaultTaxRate ?? 13),
        ref_no: initial.ref_no || "",
        memo: initial.memo || "",
        receipt: initial.has_receipt ? "keep" : null,
      });
    } else {
      setForm(emptyForm(defaultTaxRate));
    }
    api.get(`${base}/expenses/suggestions`).then((r) => setSugg(r.data || {})).catch(() => {});
  }, [base, open, initial, defaultTaxRate]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setLine = (i, k, v) => setForm((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setForm((s) => ({ ...s, lines: [...s.lines, emptyLine()] }));
  const removeLine = (i) => setForm((s) => ({ ...s, lines: s.lines.length > 1 ? s.lines.filter((_, j) => j !== i) : s.lines }));

  const pickFile = (accept, capture) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = accept;
    if (capture) inp.capture = capture;
    inp.onchange = () => {
      const f = inp.files?.[0];
      if (!f) return;
      if (f.size > 10 * 1024 * 1024) return toast.error("Receipt must be under 10 MB");
      const r = new FileReader();
      r.onload = () => set("receipt", String(r.result));
      r.readAsDataURL(f);
    };
    inp.click();
  };

  const save = async () => {
    if (!form.lines.some((l) => l.category.trim())) return toast.error("Type of expense is required");
    if (totals.subtotal <= 0) return toast.error("Enter an amount");
    setSaving(true);
    try {
      const body = {
        ...form,
        tax_rate: num(form.tax_rate),
        lines: form.lines.map((l) => ({ ...l, amount: num(l.amount) })),
      };
      if (isEdit) {
        await api.put(`${base}/expenses/${initial.expense_id}`, body);
        toast.success("Expense updated");
      } else if (fromTx) {
        await api.post(`${bankBase}/transactions/${fromTx.transaction_id}/expense`, body);
        toast.success("Expense saved and matched to the bank transaction");
      } else {
        await api.post(`${base}/expenses`, body);
        toast.success("Expense saved");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save expense");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" data-testid="pex-dialog">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-xl border border-white/10 bg-[#0b0b0f] text-white sm:rounded-lg">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <h3 className="text-base font-bold uppercase tracking-tight">{isEdit ? "Edit expense" : "New expense"}</h3>
          <button type="button" onClick={onClose} data-testid="pex-close" className="rounded-md p-1 text-white/60 hover:bg-white/10 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* Payment method */}
          <div>
            <label className={labelCls}>How did you pay?</label>
            <div className="mt-1 grid grid-cols-4 gap-1 rounded-md border border-white/10 bg-white/[0.02] p-1" role="radiogroup">
              {PAY_METHODS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  role="radio"
                  aria-checked={form.payment_method === m.key}
                  onClick={() => set("payment_method", m.key)}
                  data-testid={`pex-method-${m.key}`}
                  className={`rounded px-2 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    form.payment_method === m.key ? "bg-[#1a3fd1] text-white shadow" : "text-white/60 hover:text-white"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>From (account)</label>
              <input value={form.account} onChange={(e) => set("account", e.target.value)} list="pex-accounts" autoComplete="off" placeholder="Which card or account?" data-testid="pex-account" className={`mt-1 ${inputCls}`} />
              <datalist id="pex-accounts">{(sugg.accounts || []).map((a) => <option key={a} value={a} />)}</datalist>
            </div>
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="pex-date" className={`mt-1 ${inputCls}`} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Who you paid</label>
            <input value={form.payee} onChange={(e) => set("payee", e.target.value)} list="pex-payees" autoComplete="off" placeholder="Petro Canada, Home Depot…" data-testid="pex-payee" className={`mt-1 ${inputCls}`} />
            <datalist id="pex-payees">{(sugg.payees || []).map((a) => <option key={a} value={a} />)}</datalist>
          </div>

          {/* Splits */}
          <div className="space-y-3">
            {form.lines.map((l, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`pex-line-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Amount $</label>
                      <input type="text" inputMode="decimal" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" data-testid={`pex-line-${i}-amount`} className={`mt-1 ${inputCls} font-mono`} />
                    </div>
                    <div>
                      <label className={labelCls}>Type of expense</label>
                      <input value={l.category} onChange={(e) => setLine(i, "category", e.target.value)} list="pex-categories" autoComplete="off" placeholder="Fuel & Oil, Tools…" data-testid={`pex-line-${i}-category`} className={`mt-1 ${inputCls}`} />
                    </div>
                    <div>
                      <label className={labelCls}>Who was it for?</label>
                      <input value={l.who_for} onChange={(e) => setLine(i, "who_for", e.target.value)} list="pex-whofor" autoComplete="off" placeholder="Optional" data-testid={`pex-line-${i}-who`} className={`mt-1 ${inputCls}`} />
                    </div>
                    <div>
                      <label className={labelCls}>Description</label>
                      <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Optional" data-testid={`pex-line-${i}-desc`} className={`mt-1 ${inputCls}`} />
                    </div>
                  </div>
                  {form.lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(i)} data-testid={`pex-line-${i}-remove`} aria-label="Remove split" className="mt-5 rounded-md p-1.5 text-white/50 hover:bg-white/10 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <datalist id="pex-categories">{(sugg.categories || []).map((a) => <option key={a} value={a} />)}</datalist>
            <datalist id="pex-whofor">{(sugg.who_for || []).map((a) => <option key={a} value={a} />)}</datalist>
            <button type="button" onClick={addLine} data-testid="pex-add-split" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[#8fa4f0] hover:text-white">
              <Plus className="h-3.5 w-3.5" /> Add a split
            </button>
          </div>

          {/* Tax */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
            <div>
              <label className={labelCls}>Tax is</label>
              <select value={form.tax_mode} onChange={(e) => set("tax_mode", e.target.value)} data-testid="pex-tax-mode" className={`mt-1 ${inputCls} bg-[#0a0a0a]`}>
                {TAX_MODES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Rate %</label>
              <input type="text" inputMode="decimal" value={form.tax_rate} disabled={form.tax_mode === "none"} onChange={(e) => set("tax_rate", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="pex-tax-rate" className={`mt-1 ${inputCls} font-mono disabled:opacity-40`} />
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 font-mono text-sm" data-testid="pex-totals">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{fmtMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between text-white/60"><span>Tax{form.tax_mode !== "none" ? ` (${num(form.tax_rate)}%)` : ""}</span><span>{fmtMoney(totals.tax)}</span></div>
            <div className="mt-1 flex justify-between border-t border-white/10 pt-1 text-base font-bold text-white"><span>Total</span><span data-testid="pex-total">{fmtMoney(totals.total)}</span></div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Ref no.</label>
              <input value={form.ref_no} maxLength={21} onChange={(e) => set("ref_no", e.target.value)} placeholder="21 characters max" data-testid="pex-ref" className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Message on statement</label>
              <input value={form.memo} onChange={(e) => set("memo", e.target.value)} data-testid="pex-memo" className={`mt-1 ${inputCls}`} />
            </div>
          </div>

          {/* Receipt */}
          <div>
            <label className={labelCls}>Receipt (optional)</label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => pickFile("image/*", "environment")} data-testid="pex-capture" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-white/25">
                <Camera className="h-3.5 w-3.5" /> Take photo
              </button>
              <button type="button" onClick={() => pickFile("image/*,application/pdf", null)} data-testid="pex-upload" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-white/25">
                <Upload className="h-3.5 w-3.5" /> Upload file
              </button>
              {form.receipt && (
                <span className="inline-flex items-center gap-1 font-mono text-[10px] text-emerald-300" data-testid="pex-receipt-attached">
                  <Paperclip className="h-3 w-3" /> receipt attached
                  <button type="button" onClick={() => set("receipt", null)} data-testid="pex-receipt-remove" className="ml-1 text-white/50 hover:text-red-300" aria-label="Remove receipt"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button onClick={onClose} data-testid="pex-cancel" className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pex-save" className="rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60">
            {saving ? "Saving…" : "Save expense"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalExpenseDialog;
